/**
 * Client-side validation shared by every photo upload surface (PhotoUploadDropzone
 * and the gallery's page-level drop target). Mirrors the backend limits in
 * src/middleware/fileUpload.middleware.ts (ALLOWED_MIME_TYPES, MAX_FILE_SIZE_BYTES).
 */
export const PHOTO_MAX_SIZE_BYTES = 10 * 1024 * 1024;

export const PHOTO_ACCEPTED_MIME_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/webp'];

const ACCEPTED_MIME_SET = new Set(PHOTO_ACCEPTED_MIME_TYPES);

/**
 * File-picker filter. HEIC/HEIF (iPhone photos) is offered too: the dropzone converts
 * it to JPEG before validation (heicConversion.ts), so it never reaches the backend.
 */
export const PHOTO_ACCEPT_STRING = [...PHOTO_ACCEPTED_MIME_TYPES, 'image/heic', 'image/heif', '.heic', '.heif'].join(',');

export type PhotoFileRejectionReason = 'type' | 'size';

export interface PhotoFileRejection {
  file: File;
  reason: PhotoFileRejectionReason;
}

export function isAcceptedPhotoType(mimeType: string): boolean {
  return ACCEPTED_MIME_SET.has(mimeType);
}

/** Returns why a file cannot be uploaded, or null when it is acceptable. */
export function getPhotoFileRejection(
  file: File,
  maxBytes: number = PHOTO_MAX_SIZE_BYTES,
): PhotoFileRejectionReason | null {
  if (!ACCEPTED_MIME_SET.has(file.type)) return 'type';
  if (file.size > maxBytes) return 'size';
  return null;
}

/** Splits a selection into uploadable files and per-file rejections, keeping order. */
export function splitPhotoFiles(
  files: readonly File[],
  maxBytes: number = PHOTO_MAX_SIZE_BYTES,
): { accepted: File[]; rejected: PhotoFileRejection[] } {
  const accepted: File[] = [];
  const rejected: PhotoFileRejection[] = [];
  for (const file of files) {
    const reason = getPhotoFileRejection(file, maxBytes);
    if (reason === null) accepted.push(file);
    else rejected.push({ file, reason });
  }
  return { accepted, rejected };
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
